/**
 * OutboundCommunicationsWarning · ARC-014 misconfiguration banner.
 *
 * Renders a prominent warning at the top of the Administration page
 * whenever the running instance is not configured to transmit outbound
 * communications. Purely presentational — the actual gate remains
 * enforced by the backend `environment.py` module.
 *
 * Visibility rules:
 *   - Renders only when `outbound_communications === "SUPPRESSED"`
 *   - Callers must guard the role themselves (this component is
 *     already inside Administration, which is Administrator-only)
 */
import { useEffect, useState } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function OutboundCommunicationsWarning() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/system/about");
        if (alive) setInfo(data);
      } catch {
        // If the About endpoint is unreachable, we intentionally show
        // nothing — the operator has bigger problems than a banner.
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!info) return null;
  if (info.outbound_communications === "ENABLED") return null;

  return (
    <div
      data-testid="outbound-communications-warning"
      role="alert"
      className="mb-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm"
    >
      <WarningCircle size={22} weight="fill" className="shrink-0 text-amber-600" />
      <div className="flex-1">
        <div className="font-semibold" data-testid="outbound-warning-title">
          Outbound communications are currently suppressed
        </div>
        <p className="mt-1 leading-relaxed">
          The running instance reports{" "}
          <code className="rounded bg-white px-1.5 py-0.5 text-[12px] font-semibold text-amber-900">
            environment: {info.environment}
          </code>{" "}
          and{" "}
          <code className="rounded bg-white px-1.5 py-0.5 text-[12px] font-semibold text-amber-900">
            outbound: SUPPRESSED
          </code>
          . No Daily Pricing, price notification, or other outbound email
          will be transmitted until the production pod has{" "}
          <code className="rounded bg-white px-1 font-semibold">ENVIRONMENT=production</code>{" "}
          and{" "}
          <code className="rounded bg-white px-1 font-semibold">EMAIL_DELIVERY_ENABLED=true</code>{" "}
          set. Verify the production environment configuration before
          attempting to distribute Daily Pricing.
        </p>
      </div>
    </div>
  );
}
